import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Boxes, Search, Grid3X3, Eye } from 'lucide-react';
import './NativeItems.css';

// Items (Native) — read-only item + BOM browser. The screen replacing
// "open the item in NetSuite": search the synced item master, click through
// to the full record (warehouse panel, BOM, where-used, bins, cost, history).

const ITEM_TYPES = [
  { value: '', label: 'All types' },
  { value: 'Assembly', label: 'Assembly' },
  { value: 'InvtPart', label: 'Inventory Part' },
  { value: 'NonInvtPart', label: 'Non-inventory' },
  { value: 'Kit', label: 'Kit' },
  { value: 'Service', label: 'Service' },
];

const fmtQty = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

export default function NativeItemsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState(params.get('q') || '');
  const [type, setType] = useState(params.get('type') || '');
  const [includeInactive, setIncludeInactive] = useState(params.get('inactive') === 'true');
  const [page, setPage] = useState(Math.max(1, parseInt(params.get('page'), 10) || 1));
  const [result, setResult] = useState({ items: [], total: 0, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [binCode, setBinCode] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get('/api/native-items', {
          params: {
            q: query.trim() || undefined,
            type: type || undefined,
            includeInactive: includeInactive ? 'true' : undefined,
            page,
            pageSize: 50,
          },
        });
        setResult(data);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, type, includeInactive, page]);

  // Keep the URL shareable.
  useEffect(() => {
    const next = {};
    if (query.trim()) next.q = query.trim();
    if (type) next.type = type;
    if (includeInactive) next.inactive = 'true';
    if (page > 1) next.page = String(page);
    setParams(next, { replace: true });
  }, [query, type, includeInactive, page, setParams]);

  const totalPages = Math.max(1, Math.ceil((result.total || 0) / (result.pageSize || 50)));

  const goBin = (e) => {
    e.preventDefault();
    if (binCode.trim()) navigate(`/native-items/bin/${encodeURIComponent(binCode.trim())}`);
  };

  return (
    <div className="nit-page">
      <div className="nit-header">
        <h1><Boxes size={22} /> Items (Native)</h1>
        <p className="nit-sub">
          Item + BOM browser on the native platform — the read-only replacement for opening the item in NetSuite.
          Served from the synced database mirror. <span className="nit-readonly"><Eye size={12} /> read-only</span>
        </p>
      </div>

      {error && <div className="nit-error">{error}</div>}

      <div className="nit-card">
        <div className="nit-search-row">
          <div className="nit-search">
            <Search size={16} className="nit-search-icon" />
            <input
              className="nit-input nit-input-search"
              placeholder="Search items by SKU or name…"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              autoFocus
            />
          </div>
          <select className="nit-input nit-select" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
            {ITEM_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <label className="nit-check">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => { setIncludeInactive(e.target.checked); setPage(1); }}
            />
            Include inactive
          </label>
        </div>

        {loading && result.items.length === 0 ? (
          <div className="nit-loading">Loading items…</div>
        ) : result.items.length === 0 ? (
          <div className="nit-empty">No items match. Try a different search, or include inactive items.</div>
        ) : (
          <div className="nit-table-wrap">
            <table className="nit-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th className="r">On hand</th>
                  <th className="r">Cost</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {result.items.map((it) => (
                  <tr
                    key={it.id}
                    className={`nit-row-link ${it.isActive ? '' : 'nit-inactive-row'}`}
                    onClick={() => navigate(`/native-items/${encodeURIComponent(it.sku || it.id)}`)}
                  >
                    <td><code>{it.sku}</code></td>
                    <td>{it.name}</td>
                    <td><span className={`nit-tag ${/assembly/i.test(it.itemType || '') ? 'nit-tag-assembly' : ''}`}>{it.itemType || '—'}</span></td>
                    <td className={`r ${Number(it.onHand) < 0 ? 'nit-neg' : ''}`}>{fmtQty(it.onHand)}</td>
                    <td className="r nit-muted">{fmtMoney(it.currentCost ?? it.averageCost)}</td>
                    <td>{!it.isActive && <span className="nit-tag nit-tag-inactive">inactive</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="nit-pager">
          <span>{result.total?.toLocaleString()} items{loading ? ' · loading…' : ''}</span>
          <div className="nit-pager-btns">
            <button className="nit-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <span style={{ alignSelf: 'center' }}>Page {page} of {totalPages}</span>
            <button className="nit-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      <div className="nit-card">
        <h3>Bin lookup</h3>
        <form className="nit-search-row" onSubmit={goBin}>
          <div className="nit-search">
            <Grid3X3 size={16} className="nit-search-icon" />
            <input
              className="nit-input nit-input-search"
              placeholder="Enter a bin code (e.g. A1-01) to see its contents…"
              value={binCode}
              onChange={(e) => setBinCode(e.target.value)}
            />
          </div>
          <button type="submit" className="nit-btn nit-btn-primary" disabled={!binCode.trim()}>Look up bin</button>
        </form>
      </div>
    </div>
  );
}
